import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  merge,
  Subject,
  Subscription,
} from 'rxjs';
import { skip } from 'rxjs/operators';
import { CurrencyUomService } from 'src/app/core/services/currency-uom.service';
import { FiltersStore } from 'src/app/core/stores/filters.store';
import { TVDService } from '../services/tvd.service';
import {
  TvdByProgramData,
  TvdByProgramDetailsData,
  TvdProgramTableFilterState,
} from '../types/tvd-types';
/**
 * Store for fetching and caching TVD for programs data.
 * Automatically invalidates all caches when global filters change or currency/UOM changes.
 */
// TODO: Further recommended refactoring to make store declarative -
// Refactor store to automatically refetch programs data on
// filters/currency/UOM changes.
// Components should subscribe only to data streams, not cache invalidation events.
@Injectable({
  providedIn: 'root',
})
export class TvdProgramsStore {
  // Single subscription for all cache invalidation triggers
  private _cacheInvalidationEffectSub: Subscription;
  readonly cacheInvalidated$ = new Subject<void>();

  private readonly tvdProgramsData = new BehaviorSubject<TvdByProgramData[]>(
    [],
  );
  private _hasTvdProgramsDataLoaded = false;

  // Caching program details data by program name to avoid repeated calls when navigating between details pages of different programs
  private readonly _tvdProgramDetailsCache = new Map<
    string,
    TvdByProgramDetailsData
  >();

  // Filter state persisted across navigation so the table restores its last state on back-navigation
  private _programTableFilterState: TvdProgramTableFilterState | null = null;

  constructor(
    private _tvdService: TVDService,
    private _filtersStore: FiltersStore,
    private _currencyUomService: CurrencyUomService,
  ) {
    // Combine filters and currency/UOM changes into a single cache invalidation stream
    const filtersChanged$ = this._filtersStore.selectedFilters$.pipe(skip(1));
    const currencyUomChanged$ = combineLatest([
      this._currencyUomService.selectedCurrency$,
      this._currencyUomService.selectedUom$,
    ]).pipe(skip(1));

    this._cacheInvalidationEffectSub = merge(
      filtersChanged$,
      currencyUomChanged$,
    ).subscribe(() => {
      this.clearAllTvdProgramCaches();
      this.cacheInvalidated$.next();
    });
  }

  clearAllTvdProgramCaches(): void {
    this.clearTvdProgramsDataCache();
    this.clearAllTvdProgramDetailsCache();
    this._programTableFilterState = null;
  }

  // #region Manage TVD Programs Data
  /**
   * Loads TVD program data from the service if not already cached.
   * Returns the cached data observable immediately if already loaded.
   */
  async loadTvdProgramsData(): Promise<void> {
    if (this._hasTvdProgramsDataLoaded) {
      return;
    }

    const data = await this._tvdService.getTvdProgramTableData();
    const programs = Array.isArray(data) ? data : [];
    this.tvdProgramsData.next(programs);
    this._hasTvdProgramsDataLoaded = true;
  }

  /**
   * Gets the current cached program data synchronously.
   */
  getProgramsDataValue(): TvdByProgramData[] {
    return this.tvdProgramsData.value;
  }

  /**
   * Clears the cached data and resets the loaded flag.
   * Forces a fresh fetch on the next loadTvdProgramsData() call.
   */
  clearTvdProgramsDataCache(): void {
    this.tvdProgramsData.next([]);
    this._hasTvdProgramsDataLoaded = false;
  }
  // #endregion

  // #region Manage TVD Program Details Data
  /**
   * Loads TVD program details data for a given program name from the service if not already cached.
   * Returns immediately with the cached response if already loaded.
   */
  async loadTvdProgramDetailsData(
    programName: string,
  ): Promise<TvdByProgramDetailsData | undefined> {
    if (this._tvdProgramDetailsCache.has(programName)) {
      return this._tvdProgramDetailsCache.get(programName);
    }

    const data = await this._tvdService.getTvdProgramDetailsData(programName);
    if (data) {
      this._tvdProgramDetailsCache.set(programName, data);
    }
    return data;
  }

  /**
   * Clears all cached program details.
   * Forces a fresh fetch for all programs on subsequent loadTvdProgramDetailsData() calls.
   */
  clearAllTvdProgramDetailsCache(): void {
    this._tvdProgramDetailsCache.clear();
  }

  // #endregion

  // #region TVD Program Table Filter State
  saveProgramTableFilterState(state: TvdProgramTableFilterState): void {
    this._programTableFilterState = { ...state };
  }

  getProgramTableFilterState(): TvdProgramTableFilterState | null {
    return this._programTableFilterState;
  }
  // #endregion
}
